// ============================================
// Deterministic Scoring Engine
// This MUST match the server-side implementation exactly
// ============================================

import type {
  ChecklistResponse,
  ChecklistStatus,
  RiskBand,
  ScoringResult,
  CategoryScore,
  Severity,
  ChecklistCategory,
} from '../types';
import {
  COMPLIANCE_CHECKLIST,
  SCORING_CONFIG,
} from '../config/checklist';

// ============================================
// SCORING ALGORITHM
// ============================================

/**
 * Calculate the base points for a given status
 */
function getBasePoints(status: ChecklistStatus): number {
  switch (status) {
    case 'COMPLIANT':
      return SCORING_CONFIG.compliantPoints;
    case 'AT_RISK':
      return SCORING_CONFIG.atRiskPoints;
    case 'NON_COMPLIANT':
      return SCORING_CONFIG.nonCompliantPoints;
    case 'UNKNOWN':
    default:
      return SCORING_CONFIG.unknownPoints;
  }
}

/**
 * Get the severity weight multiplier
 */
function getSeverityWeight(severity: Severity): number {
  return SCORING_CONFIG.severityWeights[severity];
}

/**
 * Determine risk band from total score
 */
function determineRiskBand(totalScore: number): RiskBand {
  const { bands } = SCORING_CONFIG;

  if (totalScore <= bands.low.max) {
    return 'low';
  } else if (totalScore <= bands.medium.max) {
    return 'medium';
  } else {
    return 'high';
  }
}

/**
 * Main scoring calculation function
 * This is deterministic - same inputs always produce same outputs
 * @param responses - The user's checklist responses
 * @param checklist - Optional state-specific checklist (defaults to COMPLIANCE_CHECKLIST)
 */
export function calculateScore(
  responses: Record<string, ChecklistResponse>,
  checklist?: ChecklistCategory[]
): ScoringResult {
  const categories = checklist || COMPLIANCE_CHECKLIST;
  let totalScore = 0;
  const categoryScores: Record<string, CategoryScore> = {};
  const breakdown = {
    compliant: 0,
    atRisk: 0,
    nonCompliant: 0,
    unknown: 0,
  };

  // Calculate scores for each category (uses state-specific checklist if provided)
  for (const category of categories) {
    let categoryScore = 0;
    let maxPossible = 0;

    for (const item of category.items) {
      const response = responses[item.id];
      const severityWeight = getSeverityWeight(item.severity);

      // Max possible is if everything was non-compliant
      maxPossible += SCORING_CONFIG.nonCompliantPoints * severityWeight;

      // Get status (default to UNKNOWN if not answered)
      const status: ChecklistStatus = response?.status || 'UNKNOWN';
      const basePoints = getBasePoints(status);
      const itemScore = basePoints * severityWeight;

      categoryScore += itemScore;
      totalScore += itemScore;

      // Update breakdown
      switch (status) {
        case 'COMPLIANT':
          breakdown.compliant++;
          break;
        case 'AT_RISK':
          breakdown.atRisk++;
          break;
        case 'NON_COMPLIANT':
          breakdown.nonCompliant++;
          break;
        case 'UNKNOWN':
        default:
          breakdown.unknown++;
          break;
      }
    }

    // Store category score
    categoryScores[category.id] = {
      score: categoryScore,
      maxPossible,
      percentage: maxPossible > 0 ? Math.round((categoryScore / maxPossible) * 100) : 0,
    };
  }

  return {
    totalScore,
    riskBand: determineRiskBand(totalScore),
    categoryScores,
    breakdown,
    calculatedAt: new Date(),
    version: 'snapshot-2024-v1',
  };
}

// ============================================
// IMMEDIATE & FUTURE RISKS
// ============================================

interface RiskItem {
  itemId: string;
  text: string;
  category: string;
  severity: Severity;
  status: ChecklistStatus;
  weightedScore: number;
}

/**
 * Get immediate risks (NON_COMPLIANT items + top AT_RISK items)
 * @param responses - The user's checklist responses
 * @param limit - Max number of risks to return
 * @param checklist - Optional state-specific checklist (defaults to COMPLIANCE_CHECKLIST)
 */
export function getImmediateRisks(
  responses: Record<string, ChecklistResponse>,
  limit: number = 10,
  checklist?: ChecklistCategory[]
): RiskItem[] {
  const categories = checklist || COMPLIANCE_CHECKLIST;
  const allItems = categories.flatMap(cat => cat.items);
  const risks: RiskItem[] = [];

  // Get all NON_COMPLIANT items
  for (const item of allItems) {
    const response = responses[item.id];
    if (response?.status === 'NON_COMPLIANT') {
      risks.push({
        itemId: item.id,
        text: item.text,
        category: item.category,
        severity: item.severity,
        status: 'NON_COMPLIANT',
        weightedScore: SCORING_CONFIG.nonCompliantPoints * getSeverityWeight(item.severity),
      });
    }
  }

  // Add top AT_RISK items by weighted score
  const atRiskItems: RiskItem[] = [];
  for (const item of allItems) {
    const response = responses[item.id];
    if (response?.status === 'AT_RISK') {
      atRiskItems.push({
        itemId: item.id,
        text: item.text,
        category: item.category,
        severity: item.severity,
        status: 'AT_RISK',
        weightedScore: SCORING_CONFIG.atRiskPoints * getSeverityWeight(item.severity),
      });
    }
  }

  // Sort AT_RISK by weighted score (highest first) and take top 5
  atRiskItems.sort((a, b) => b.weightedScore - a.weightedScore);
  risks.push(...atRiskItems.slice(0, 5));

  // Sort all risks by weighted score and return
  risks.sort((a, b) => b.weightedScore - a.weightedScore);
  return risks.slice(0, limit);
}

/**
 * Get future risks (remaining AT_RISK + UNKNOWN items)
 * @param responses - The user's checklist responses
 * @param immediateRiskIds - IDs of items already in immediate risks
 * @param checklist - Optional state-specific checklist (defaults to COMPLIANCE_CHECKLIST)
 */
export function getFutureRisks(
  responses: Record<string, ChecklistResponse>,
  immediateRiskIds: string[],
  checklist?: ChecklistCategory[]
): RiskItem[] {
  const categories = checklist || COMPLIANCE_CHECKLIST;
  const allItems = categories.flatMap(cat => cat.items);
  const risks: RiskItem[] = [];

  for (const item of allItems) {
    const response = responses[item.id];
    const status = response?.status || 'UNKNOWN';

    // Skip if already in immediate risks
    if (immediateRiskIds.includes(item.id)) continue;

    // Include AT_RISK and UNKNOWN items not in immediate
    if (status === 'AT_RISK' || status === 'UNKNOWN') {
      const basePoints = getBasePoints(status);
      risks.push({
        itemId: item.id,
        text: item.text,
        category: item.category,
        severity: item.severity,
        status: status,
        weightedScore: basePoints * getSeverityWeight(item.severity),
      });
    }
  }

  // Sort by weighted score
  risks.sort((a, b) => b.weightedScore - a.weightedScore);
  return risks;
}

// ============================================
// PROGRESS TRACKING
// ============================================

/**
 * Calculate assessment completion progress
 * @param responses - The user's checklist responses
 * @param checklist - Optional state-specific checklist (defaults to COMPLIANCE_CHECKLIST)
 */
export function calculateProgress(
  responses: Record<string, ChecklistResponse>,
  checklist?: ChecklistCategory[]
): { answered: number; total: number; percentage: number } {
  const categories = checklist || COMPLIANCE_CHECKLIST;
  const allItems = categories.flatMap(cat => cat.items);
  const total = allItems.length;
  const answered = allItems.filter(item => responses[item.id]?.status).length;

  return {
    answered,
    total,
    percentage: total > 0 ? Math.round((answered / total) * 100) : 0,
  };
}

/**
 * Calculate category progress
 */
export function calculateCategoryProgress(
  categoryId: string,
  responses: Record<string, ChecklistResponse>,
  checklist?: ChecklistCategory[]
): { answered: number; total: number; percentage: number } {
  const categories = checklist || COMPLIANCE_CHECKLIST;
  const category = categories.find(c => c.id === categoryId);
  if (!category) {
    return { answered: 0, total: 0, percentage: 0 };
  }

  const total = category.items.length;
  const answered = category.items.filter(item => responses[item.id]?.status).length;

  return {
    answered,
    total,
    percentage: total > 0 ? Math.round((answered / total) * 100) : 0,
  };
}

/**
 * Check if all required items are answered
 * @param responses - The user's checklist responses
 * @param checklist - Optional state-specific checklist (defaults to COMPLIANCE_CHECKLIST)
 */
export function isAssessmentComplete(
  responses: Record<string, ChecklistResponse>,
  checklist?: ChecklistCategory[]
): boolean {
  const categories = checklist || COMPLIANCE_CHECKLIST;
  const allItems = categories.flatMap(cat => cat.items);
  return allItems.every(item => responses[item.id]?.status);
}

/**
 * Get count of items with UNKNOWN status
 * These indicate questions where we couldn't determine compliance
 */
export function getUnknownCount(
  responses: Record<string, ChecklistResponse>,
  checklist?: ChecklistCategory[]
): number {
  const categories = checklist || COMPLIANCE_CHECKLIST;
  const allItems = categories.flatMap(cat => cat.items);
  return allItems.filter(item =>
    !responses[item.id]?.status || responses[item.id]?.status === 'UNKNOWN'
  ).length;
}

/**
 * Get items with UNKNOWN status
 */
export function getUnknownItems(
  responses: Record<string, ChecklistResponse>,
  checklist?: ChecklistCategory[]
): { id: string; text: string; category: string }[] {
  const categories = checklist || COMPLIANCE_CHECKLIST;
  const unknownItems: { id: string; text: string; category: string }[] = [];

  for (const category of categories) {
    for (const item of category.items) {
      if (!responses[item.id]?.status || responses[item.id]?.status === 'UNKNOWN') {
        unknownItems.push({
          id: item.id,
          text: item.text,
          category: category.name,
        });
      }
    }
  }

  return unknownItems;
}
